import { OfflineFieldQueuePanel } from "@/components/OfflineFieldQueuePanel";
import { useCallback, useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Mic, ClipboardCheck, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { DarWorkspace } from "@/components/dar/DarWorkspace";
import { DarSheetHeader, DarSheetEntries, type DarSheetPayload } from "@/components/dar/DarSheetView";
import { getOpenDarSheet as getOpenDarSheetFn, listDarSheets as listDarSheetsFn } from "@/lib/dar.functions";

export const Route = createFileRoute("/_authenticated/dar/")({
  component: DarPage,
  head: () => ({
    meta: [
      { title: "D.A.R. Sheet — MissionReady" },
      {
        name: "description",
        content: "Your running shift D.A.R. Sheet: voice occurrences in chronological order, reviewed and submitted once.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function DarPage() {
  const { currentOrg } = useAuth();
  const [view, setView] = useState<DarSheetPayload | null>(null);
  const [recent, setRecent] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [justAdded, setJustAdded] = useState<string | null>(null);

  const getOpenSheet = useServerFn(getOpenDarSheetFn);
  const listSheets = useServerFn(listDarSheetsFn);

  const load = useCallback(async () => {
    if (!currentOrg) return;
    setLoading(true);
    try {
      const [sheet, sheets] = await Promise.all([
        getOpenSheet({ data: { orgId: currentOrg.id } }),
        listSheets({ data: { orgId: currentOrg.id, mine: true, limit: 10 } }),
      ]);
      setView((sheet as DarSheetPayload | null) ?? null);
      setRecent((sheets as any[]) ?? []);
    } catch (e: any) {
      toast.error(e?.message ?? "Could not load your D.A.R. Sheet");
    } finally {
      setLoading(false);
    }
  }, [currentOrg, getOpenSheet, listSheets]);

  useEffect(() => { void load(); }, [load]);

  // A DAR logged from the capture workspace redirects here; flag it so the
  // guard immediately sees the new occurrence in its chronological slot.
  useEffect(() => {
    try {
      const id = sessionStorage.getItem("dar:justAdded");
      if (id) {
        setJustAdded(id);
        sessionStorage.removeItem("dar:justAdded");
      }
    } catch {
      /* highlighting is cosmetic */
    }
  }, [view]);

  return (
    <div className="px-4 py-6 md:px-8 md:py-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-bold md:text-3xl">Daily Activity Report</h1>
          <p className="text-sm text-muted-foreground">
            Speak an occurrence, review it, log it. Everything accumulates on one shift sheet.
          </p>
        </div>
        <Button className="h-12" onClick={() => setOpen(true)}>
          <Mic className="mr-2 h-5 w-5" /> New voice memo
        </Button>
      </div>

      <OfflineFieldQueuePanel kind="dar_memo" />


      {loading && (
        <div role="status" className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading your shift…
        </div>
      )}

      {!loading && !view && (
        <div className="mt-6 rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No D.A.R. Sheet open yet. Your first logged occurrence starts today's sheet — tap “New voice memo”, or
          press and hold the mic orb anywhere in the app.
        </div>
      )}

      {!loading && view && (
        <div className="mt-6 space-y-4">
          <DarSheetHeader view={view} />
          {justAdded && view.entries.some((e) => e.id === justAdded) && (
            <p role="status" className="rounded-lg border border-primary/40 bg-primary/5 p-3 text-sm text-primary">
              DAR successfully added to your D.A.R. Sheet.
            </p>
          )}
          <DarSheetEntries
            view={view}
            allowAmend
            onChanged={() => void load()}
            highlightEntryId={justAdded}
          />

          <div className="sticky bottom-20 rounded-xl border border-border bg-card/95 p-4 backdrop-blur md:bottom-4">
            <p className="text-sm">
              <strong>{view.entries.length}</strong> occurrence{view.entries.length === 1 ? "" : "s"} on this sheet.
              Review the full shift report before you submit.
            </p>
            <Button asChild className="mt-3 h-12 w-full">
              <Link to="/dar/review">
                <ClipboardCheck className="mr-2 h-4 w-4" /> Review &amp; Submit D.A.R. Sheet
              </Link>
            </Button>
          </div>
        </div>
      )}

      {!loading && recent.length > 0 && (
        <section className="mt-8">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Submitted shift reports
          </h2>
          <div className="mt-2 space-y-2">
            {recent.map((r) => (
              <Link
                key={r.id}
                to="/dar/$reportId"
                params={{ reportId: r.id }}
                className="flex items-center gap-3 rounded-lg border border-border bg-card p-3 text-sm hover:border-primary/50"
              >
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1">
                  <span className="block font-medium">{r.shift_date ?? "—"}</span>
                  <span className="block text-xs text-muted-foreground">
                    {r.entry_count ?? 0} occurrence{(r.entry_count ?? 0) === 1 ? "" : "s"} ·{" "}
                    {r.submitted_at ? new Date(r.submitted_at).toLocaleString() : ""}
                  </span>
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <DarWorkspace open={open} onOpenChange={setOpen} onLogged={() => void load()} />
    </div>
  );
}
